import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import Field from 'components/Form/Field';
import FormActions from 'components/Form/FormActions';
import FormResult from 'components/Form/FormResult';
import InputEmail from 'components/Form/InputEmail';
import InputText from 'components/Form/InputText';
import SubmitButton from 'components/Form/SubmitButton';
import TextArea from 'components/Form/TextArea';
import SideBySide from 'components/Layout/SideBySide';
import SectionHeader from 'components/SectionHeader';
import {useBackend} from 'state/backend';
import {Backend} from 'types/api';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiError, onApiSuccess} from 'utils/request-axios/utils';
import useEditTeamMember from './services';
import {EditTeamMemberFormFields} from './types';

type TeamMemberEditFormData = FormFields<EditTeamMemberFormFields>;

export type TeamMemberEditBackendData = Backend<{
    address: string | undefined;
    companyName: string;
    eventNotificationsEmail: string;
    familyName: string;
    givenName: string;
    isThisUser: boolean;
    mid: string;
    sendingSignature: string;
    showEmailField: boolean;
    userNotificationsEmail: string;
}>;

const buildName = (...values: (string | undefined)[]) =>
    values.filter(Boolean).join(' ');

const TeamMemberEdit = () => {
    const {
        data: {
            address,
            familyName,
            givenName,
            isThisUser,
            mid,
            sendingSignature,
            showEmailField,
        },
    } = useBackend<TeamMemberEditBackendData>();

    const methods = useForm<TeamMemberEditFormData>({
        defaultValues: {
            address,
            familyName,
            givenName,
            sendingSignature,
        },
        shouldUnregister: true,
    });

    const {mutateAsync} = useEditTeamMember();

    const {handleSubmit} = methods;
    const {t} = useTranslation('Team.Member.Edit');

    const onSubmit = (formData: EditTeamMemberFormFields) => {
        mutateAsync({formData, mid})
            .then(
                onApiSuccess({
                    onSuccess: () =>
                        setUrl(window.Urls.employee_item_view(mid)),
                }),
            )
            .catch(onApiError(methods));
    };

    return (
        <FormProvider {...methods}>
            <SectionHeader title={buildName(givenName, familyName)} />
            <form onSubmit={handleSubmit(onSubmit)}>
                <SideBySide className="mt-4 max-w-xl">
                    <InputText
                        label={t('familyName')}
                        name="familyName"
                        required={true}
                    />
                    <InputText
                        label={t('givenName')}
                        name="givenName"
                        required={true}
                    />
                    {showEmailField && (
                        <InputEmail
                            label={t('address')}
                            name="address"
                            required={true}
                        />
                    )}
                    <TextArea
                        label={t('sendingSignature')}
                        name="sendingSignature"
                        rows={3}
                    />
                    <Field type="value">
                        <div>
                            {isThisUser ? (
                                <>
                                    <p>{t('info.note')}</p>
                                    <ul>
                                        <li>
                                            {t(
                                                'info.editingTeamNameNotAutomaticallyChange',
                                            )}
                                        </li>
                                        <li>
                                            <Trans
                                                components={[
                                                    <a
                                                        key={0}
                                                        href="/profile/email-settings/"
                                                    >
                                                        {' '}
                                                    </a>,
                                                ]}
                                                i18nKey="info.goToYourEmailSettings"
                                                ns="Team.Member.Edit"
                                                t={t}
                                            />
                                        </li>
                                    </ul>
                                </>
                            ) : (
                                <p>
                                    <Trans
                                        i18nKey="info.noteThatEditing"
                                        ns="Team.Member.Edit"
                                        t={t}
                                    />
                                </p>
                            )}
                        </div>
                    </Field>
                    <FormResult />
                    <FormActions>
                        <SubmitButton>{t('submit')}</SubmitButton>
                    </FormActions>
                </SideBySide>
            </form>
        </FormProvider>
    );
};

export default TeamMemberEdit;
